<template>
  <Notifications
    :message="errorMessage"
    :open="addItemFailed"
    pattern="persistent"
    type="error"
    @update:open="hasError = $event"
  />

  <Notifications
    :message="addItemAmountMsgTxtRef ?? ''"
    :open="addItemAmountMessage"
    pattern="toast"
    type="warning"
  />
  <div
    v-if="reactiveConfig.newConfigurator"
    :class="[$style.container, $style.newConfigurator]"
    @click="handleInitWithWikaProdConfNew()"
  />
  <div v-else :class="$style.container">
    <div :class="$style.hint">
      <strong>{{ reactiveConfig.hintLabel }}</strong>
      <span>{{ reactiveConfig.hintText }}</span>
    </div>
    <div
      v-if="reactiveConfig.modelcodes.length > 1"
      :class="[$style.radios, 'columns', 'is-multiline']"
    >
      <div :class="['column', 'is-full']">
        <strong>{{ reactiveConfig.pleaseChooseText }}</strong>
      </div>
      <div
        v-for="(code, index) in reactiveConfig.modelcodes"
        :key="code"
        :class="['column', 'is-half']"
      >
        <div :class="$style.radio">
          <span>
            <input
              :id="'modelcode' + index"
              v-model="reactiveConfig.modeloritemid"
              :class="$style.orderDataItem"
              name="modelcode"
              type="radio"
              :value="code"
              @change="init(reactiveConfig)"
            />
            <label :for="'modelcode' + index">
              {{ reactiveConfig.additionalTexts[index] }}
            </label>
          </span>
        </div>
      </div>
    </div>
    <div :class="$style.app" />
  </div>
</template>

<script setup>
import CustomEvents from '../../globals/constants/customEvents';
import { useStoreConfiguration } from '../../store/configuration/configuration';
import { useStoreTranslations } from '../../store/translations/translations';
import addItemToUrl from '../../xhr/shopCart/addItemToUrl';
import getQuoteIdFromUrl from '../../xhr/shopCart/getQuoteIdFromUrl';
import Notifications from '../Notifications/Notifications.vue';

import { computed, onMounted, ref } from 'vue';

const wtTrack = window.wtTrack;
const storeTranslations = useStoreTranslations();
const storeConfiguration = useStoreConfiguration();
const deliveryCategoryIncludeList = computed(() => {
  const rawValue = storeConfiguration.configShop?.deliveryCategoryThreshold;
  const threshold = Number.isInteger(rawValue) ? rawValue : null;

  let deliveryCategories;

  if (threshold === 0) {
    deliveryCategories = [0, 1, 2, 3, 4, 5];
  } else if (threshold && threshold > 0) {
    deliveryCategories = Array.from({ length: threshold }, (_, i) => i + 1);
  } else {
    deliveryCategories = [1, 2, 3, 4];
  }
  return deliveryCategories;
});

const translations = ref(storeTranslations);
const addItemFailed = ref(false);
const addItemAmountMessage = ref(false);
const addItemAmountMsgTxtRef = ref(null);
const errorMessage = ref(
  translations.value['addItem.genericError'] ||
    'Technical error – the item could not be added. Please try again later.',
);

const props = defineProps({
  config: {
    type: Object,
    default: () => {},
  },
  shopConfig: {
    type: Object,
    default: () => {},
  },
});

const emit = defineEmits(['closed', 'error', 'amount-warning']);

const delay = ref(1000);
const reactiveConfig = ref(JSON.parse(JSON.stringify(props.config)));

const initNew = (localConfig) => {
  window.onWebConfiguratorClosedOkToShopNew = (data, close, closeAll) => {
    let urlParams = '';
    let articleID = '';
    let buildID = '';

    if (!data.ItemInformation.Item.IsOrmigNumber && !data.ItemInformation.Item.ItemId) {
      articleID = data.ItemInformation.Item.ItemId;
      urlParams = '?itemid=' + articleID;
    } else {
      //getBuildID
      buildID = data.BuildId;
      urlParams = '?buildid=' + buildID;
    }
    // add delay to prevent NS_BINDING_ABORTED in firefox
    window.setTimeout(() => {
      window.location.href =
        localConfig.shopUrl +
        (typeof urlParams !== 'undefined' && urlParams !== '' ? urlParams : '');
    }, delay.value);
    close.close();
    closeAll();
  };

  // Finished configuration
  window.onWebConfiguratorClosedOkNew = (data, close, closeAll) => {
    addItemFailed.value = false;
    addItemAmountMessage.value = false;
    addItemAmountMsgTxtRef.value = props.addItemAmountMsgTxt;
    let addItemHighestAmountMsg = props.addItemHighestAmountMsg;
    let addItemLowestAmountMsg = props.addItemLowestAmountMsg;
    let addItemMultipleAmountMsg = props.addItemMultipleAmountMsg;

    errorMessage.value = '';
    const deliveryTime = parseInt(data.ItemInformation.DeliveryInformation.DeliveryTime, 10);
    const externalTransport = parseInt(
      data.ItemInformation.DeliveryInformation.ExternalTransport,
      10,
    );
    const deliveryTimeSum =
      (isNaN(deliveryTime) ? 0 : deliveryTime) + (isNaN(externalTransport) ? 0 : externalTransport);

    getQuoteIdFromUrl(window.getQuoteIdUrl)
      .then((quoteId) => {
        addItemToUrl(window.addItemUrl, {
          itemid: data.ItemInformation.Item.ItemId,
          isOrmigNumber: data.ItemInformation.Item.IsOrmigNumber,
          quantity: Number(data.Quantity || 1),
          country: window.currentCountry,
          language: window.currentLanguage,
          quoteid: quoteId,
          headline: props.shopConfig.headline,
          subheadline: props.shopConfig.subheadline,
          pictureurl: props.shopConfig.pictureurl,
          thumbnailurl: props.shopConfig.thumbnailurl,
          producturl: props.shopConfig.producturl,
          btype: data.ItemInformation.ModelcodeObj.map(
            (codeObjItem) => codeObjItem.TypeObject.CodeType,
          ),
          codestring: data.ItemInformation.ModelcodeObj.map(
            (codeObjItem) => codeObjItem.TypeObject.CodeString,
          ),
          price: data.ItemInformation.Pricing.NetPrice,
          priceSum: data.ItemInformation.Pricing.PositionPrice,
          priceInstanceSum: data.ItemInformation.Pricing.PositionPrice,
          grossPrice: data.ItemInformation.Pricing.GrossPrice,
          grossPriceUnit: data.ItemInformation.Pricing.GrossPriceUnit,
          netPriceUnit: data.ItemInformation.Pricing.NetPriceUnit,
          deliveryTime: deliveryTimeSum,
          deliveryCategory: data.ItemInformation.DeliveryInformation.DeliveryCategoryInt,
          // ? individualPrice extra
          productType: props.shopConfig.productType || data.ItemInformation.Item.ProductType,
          isLoggedIn: Boolean(window.logIn),
          discount: window.logIn?.discount,
          typesExcludedDiscount: window.logIn?.typesExcludedDiscount,
          container: window.searchContainer,
          shopContainer: window.searchShopContainer,
          terminationError: data.ItemInformation.DeliveryInformation.TerminationError,
          mainType: data.ItemInformation.Item.MainType,
          stockItem: data.ItemInformation.Item.StockItem,
        })
          .then((response) => {
            if (
              typeof response.data.amountMessage !== 'undefined' &&
              response.data.amountMessage != null &&
              response.data.amountMessage != ''
            ) {
              // ? Try to get message and value which is separated by semicolon in json response

              let strAmountMessage = '';
              let partsOfAmountMessage = response.data.amountMessage.split(';');
              if (partsOfAmountMessage[0].includes('highestAmount')) {
                strAmountMessage = addItemHighestAmountMsg.replace(
                  '###amountValue###',
                  partsOfAmountMessage[1],
                );
              }
              if (partsOfAmountMessage[0].includes('lowestAmount')) {
                strAmountMessage = addItemLowestAmountMsg.replace(
                  '###amountValue###',
                  partsOfAmountMessage[1],
                );
              }
              if (partsOfAmountMessage[0].includes('multipleAmount')) {
                strAmountMessage = addItemMultipleAmountMsg.replace(
                  '###amountValue###',
                  partsOfAmountMessage[1],
                );
              }
              addItemAmountMsgTxtRef.value = strAmountMessage;
              if (!props.disableAmountSnackbar) {
                addItemAmountMessage.value = true;
              }
              emit('amount-warning', strAmountMessage);
            }

            // Add source to response data and clean item IDs
            const eventData = {
              ...response.data,
              source: 'Configurator',
            };

            //Also add source to each item
            if (eventData.items && Array.isArray(eventData.items)) {
              eventData.items = eventData.items?.map((item) => ({
                ...item,
                id: item.id?.replace(/^none_/, '') || item.id, // Remove 'none_' prefix
                source: 'Configurator',
              }));
            }

            const event = new CustomEvent(CustomEvents.ADD_TO_CART, {
              detail: eventData,
            });
            window.dispatchEvent(event);

            const updateCartEvent = new CustomEvent(CustomEvents.UPDATE_SHOP_CART, {});

            window.dispatchEvent(updateCartEvent);

            emit('closed');
            //Close configurator at the moment
            close.close();
            closeAll();
          })
          .catch((error) => {
            showErrorSnackbar(error);
          });
      })
      .catch((error) => {
        showErrorSnackbar(error);
      });
  };
  // Find similar products
  window.onWebConfiguratorClosedOkToWebformNew = function (data, close, closeAll) {
    let codeStrings = '';
    let codeNames = '';
    let articleID = '';
    let descText = '';
    if (data.CodeComplete) {
      let codes = data.ItemInformation.ModelcodeObj;
      let c = 0;
      for (c = 0; c < codes.length; c++) {
        if (c != codes.length - 1) {
          codeStrings += codes[c].TypeObject.CodeString + ',';
          codeNames += codes[c].TypeObject.CodeType + ',';
        } else {
          codeStrings += codes[c].TypeObject.CodeString;
          codeNames += codes[c].TypeObject.CodeType;
        }
      }
      if (!data.ItemInformation.Item.IsOrmigNumber) {
        articleID = data.ItemInformation.Item.ItemId;
      }
      descText = data.ItemInformation.Description.LongText;
    } else {
      let codes = data.CurrentCoding;
      let c = 0;
      for (c = 0; c < codes.length; c++) {
        codeStrings += codes[c].ContentString;
        codeNames += codes[c].CodeTypeId;
      }
      descText = data.CurrentDescriptionLong;
    }
    let codeQueryString = '?codeNames=' + codeNames + '&codes=' + codeStrings;

    if (articleID == '' || articleID == 'undefined') {
      articleID = codeStrings;
    }
    const event = new CustomEvent(CustomEvents.ADD_TO_LOCAL_STORAGE, {
      detail: {
        id: articleID,
        detailText: descText,
      },
    });
    window.dispatchEvent(event);
    // add delay to prevent NS_BINDING_ABORTED in firefox
    window.setTimeout(() => {
      window.location.href = localConfig.orderFormUrl + codeQueryString + '&article=' + articleID;
    }, delay.value);
    close.close();
    closeAll();
  };

  const ibt_btn_config = {
    label: localConfig.button_datasheet_label,
    class: 'blue',
    icon: 'download',
    callbackInternal: 'getDatasheet',
    disabledWhileIncomplete: false,
  };
  const toShop_btn_config = {
    label: localConfig.button_enquiry_label,
    class: 'blue',
    icon: 'quit',
    callbackExternal: 'onWebConfiguratorClosedOkToWebformNew',
    pregenerateBuildId: true,
    disabledWhileIncomplete: false,
    overwrites: {
      conditions: {
        isComplete: true,
        hasValidTermination: true,
        deliveryCategories: deliveryCategoryIncludeList,
      },
      values: {
        label: localConfig.button_to_shop_label,
        icon: 'quit',
        class: '',
        callbackExternal: 'onWebConfiguratorClosedOkToShopNew',
        disabledWhileIncomplete: true,
        pregenerateBuildId: true,
      },
    },
  };

  const toWebform_btn_config = {
    label: localConfig.button_enquiry_label,
    class: 'blue',
    icon: 'quit',
    callbackExternal: 'onWebConfiguratorClosedOkToWebformNew',
    disabledWhileIncomplete: false,
  };

  const reset_btn_config = {
    label: localConfig.button_reset_label,
    class: 'red',
    icon: 'reset',
    disabledWhileIncomplete: false,
    callbackInternal: 'resetConfiguration',
  };
  const help_btn_config = {
    label: localConfig.button_help_label,
    class: 'blue',
    icon: 'help',
    callbackInternal: 'helpModel',
    showSeparatorAbove: true,
  };
  const addBasket_btn_config = {
    label: localConfig.primaryButton_label,
    icon: 'cart',
    class: '',
    callbackExternal: 'onWebConfiguratorClosedOkNew',
    disabled: true,
    disabledWhileIncomplete: true,
    overwrites: {
      conditions: {
        isComplete: true,
        hasValidTermination: true,
        deliveryCategories: deliveryCategoryIncludeList,
      },
      values: {
        label: localConfig.primaryButton_label,
        class: '',
        icon: 'cart',
        callbackExternal: 'onWebConfiguratorClosedOkNew',
        disabledWhileIncomplete: true,
        disabled: false,
      },
    },
  };
  let primaryBtn = {};
  if (window.instance === 'shop') {
    primaryBtn = addBasket_btn_config;
  } else {
    primaryBtn = toShop_btn_config;
  }

  const download_3d_html =
    '<div data-form-id="' +
    localConfig.downloadSnippetHTML3DViewer.formId +
    '" data-form-api-url="' +
    localConfig.downloadSnippetHTML3DViewer.formApiUrl +
    '" data-cached-form-url="' +
    localConfig.downloadSnippetHTML3DViewer.cacheFormUrl +
    '"></div>' +
    '<script src="' +
    localConfig.downloadSnippetHTML3DViewer.scriptFormLoaderUrl +
    '"/>' +
    '<script>' +
    localConfig.downloadSnippetHTML3DViewer.scriptLanguageCode +
    '<\/script>'; // eslint-disable-line no-useless-escape

  const more_btn_config = [];
  if (localConfig.display_ibd_btn) {
    more_btn_config.push(ibt_btn_config);
  }
  if (localConfig.display_enquiry_btn && window.instance === 'website') {
    more_btn_config.push(toWebform_btn_config);
  }
  more_btn_config.push(help_btn_config);
  more_btn_config.push(reset_btn_config);

  let idvalue = '';
  let model_variants = localConfig.model_variants;
  if (localConfig.model_variants.length == 1) {
    idvalue = localConfig.model_variants[0].model;
    model_variants = [];
  }
  if (localConfig?.modelcodeobj?.length > 0) {
    model_variants = [];
    idvalue = localConfig.modelcodeobj.find(Boolean).TypeObject.CodeType;
  }
  let isPricingAllowed = window.instance === 'shop' ? true : false;

  const configValue = {
    lng: window.currentLanguage.toLowerCase(),
    langAPI: localConfig.language.toLowerCase(),
    config: {
      LiveEnvPath: localConfig.webservice_url,
      buildIdPath: localConfig.buildidpath,
      getBuildIdValues: localConfig.getbuildidvalues,
      optionGraphicSettings: localConfig.publicShareBaseUrl,
      mailSupport: localConfig.mailSupport,
      hotlinePhone: localConfig.hotlinePhone,
      qrURI: localConfig.qrURI,
      qrIdURI: localConfig.qrIdURI,
      company: localConfig.copyright,
      welcomeMessage: '',
      cadenasBaseUrl: localConfig.cadenasBaseUrl,
    },
    'data-config': {
      hideAssemblies: true,
      Id: idvalue,
      modelVariants: model_variants,
      modelCodeObj: localConfig.modelcodeobj,
      maxQty: localConfig.maxRangeQuantity,
      minQty: 0,
      //buildId: "XXXXXXX",
      /*
      modelCodeObj:[
              {
                "TypeObject":
                  {
                    "CodeType":"100.XX",
                    "CodeString":"100.01D-BG310   -A  U-AJZZ -A1ZZZZZZZZZ-",
                    "FreeTexts":[]
                  }
              },
              {
                "TypeObject":
                {
                  "CodeType":"LABEL",
                  "CodeString":"LABEL-E01DE-ZZZZZ",
                  "FreeTexts":[]
                }
              }
          ],
      */
      displayDeviceController: localConfig.displayDeviceController,
      web3dViewerUrl: localConfig.web3dViewerUrl,
      downloadFormSnippet: download_3d_html,
      langPath: localConfig.langPath,
      assetBaseUrl: localConfig.assetBaseUrl,
      contentStringType: localConfig.contentStringType,
      showAdministratorWidget: localConfig.showadminwidget,
      autoUpdateDrawings: localConfig.autoupdatedrawings,
      displayDispatchInformation: localConfig.display_despatch_information,
      isDeliveryDateExp: localConfig.is_delivery_date_exp,
      stockOnHandVisible: localConfig.stockOnHandVisible,
      shippingDetailUrl: window.shippingURL,
      customerNumber: localConfig.customer_number,
      loginName: localConfig.loginname,
      isLoggedIn: window.configuratorLoggedIn,
      websessionId: localConfig.websessionid,
      environment: localConfig.environment,
      //tenant: localConfig.erp_tenant,
      //region: localConfig.region,
      //qrcode: { mode: 'url', size: 400 },
      zIndex: parseInt(localConfig.zIndex),
      isPriceDetailsVisible: localConfig.display_price_details,
      displayViewSwitch: localConfig.display_view_switch,
      supportEmail: localConfig.support_email,
      supportPhone: localConfig.support_phone,
      supportChat: localConfig.support_chat,
      showPricing: isPricingAllowed,
      showOptionId: localConfig.showOptionId,
    },
    'data-action-buttons': {
      primaryButton: primaryBtn,
      moreOptionButtonActions: more_btn_config,
    },
  };
  window.Configurator.open(configValue);
};

const init = (localConfig) => {
  // Online-Shop
  window.onWebConfiguratorClosedOkToShop = (data) => {
    let articleID = '';
    if (!data.ExtendedInformation.ItemInformation.Item.IsOrmigNumber) {
      articleID = data.ExtendedInformation.ItemInformation.Item.ItemId;
    }
    // add delay to prevent NS_BINDING_ABORTED in firefox
    window.setTimeout(() => {
      window.location.href =
        localConfig.shopUrl +
        (typeof articleID !== 'undefined' && articleID !== '' ? '?itemid=' + articleID : '');
    }, delay.value);
  };
  // Finished configuration
  window.onWebConfiguratorClosedOk = (data) => {
    addItemFailed.value = false;
    errorMessage.value = '';
    const deliveryTime = parseInt(data.ItemInformation.DeliveryInformation.DeliveryTime, 10);
    const externalTransport = parseInt(
      data.ItemInformation.DeliveryInformation.ExternalTransport,
      10,
    );
    const deliveryTimeSum =
      (isNaN(deliveryTime) ? 0 : deliveryTime) + (isNaN(externalTransport) ? 0 : externalTransport);

    getQuoteIdFromUrl(window.getQuoteIdUrl)
      .then((quoteId) => {
        addItemToUrl(window.addItemUrl, {
          itemid: data.ItemInformation.Item.ItemId,
          isOrmigNumber: data.ItemInformation.Item.IsOrmigNumber,
          quantity: Number(data.Quantity || 1),
          country: window.currentCountry,
          language: window.currentLanguage,
          quoteid: quoteId,
          headline: props.shopConfig.headline,
          subheadline: props.shopConfig.subheadline,
          pictureurl: props.shopConfig.pictureurl,
          thumbnailurl: props.shopConfig.thumbnailurl,
          producturl: props.shopConfig.producturl,
          btype: data.ItemInformation.ModelcodeObj.map(
            (codeObjItem) => codeObjItem.TypeObject.CodeType,
          ),
          codestring: data.ItemInformation.ModelcodeObj.map(
            (codeObjItem) => codeObjItem.TypeObject.CodeString,
          ),
          price: data.ItemInformation.Pricing.NetPrice,
          priceSum: data.ItemInformation.Pricing.PositionPrice,
          priceInstanceSum: data.ItemInformation.Pricing.PositionPrice,
          grossPrice: data.ItemInformation.Pricing.GrossPrice,
          grossPriceUnit: data.ItemInformation.Pricing.GrossPriceUnit,
          netPriceUnit: data.ItemInformation.Pricing.NetPriceUnit,
          deliveryTime: deliveryTimeSum,
          deliveryCategory: data.ItemInformation.DeliveryInformation.DeliveryCategoryInt,
          // ? individualPrice extra
          productType: props.shopConfig.productType || data.ItemInformation.Item.ProductType,
          isLoggedIn: Boolean(window.logIn),
          discount: window.logIn?.discount,
          typesExcludedDiscount: window.logIn?.typesExcludedDiscount,
          container: window.searchContainer,
          shopContainer: window.searchShopContainer,
          terminationError: data.ItemInformation.DeliveryInformation.TerminationError,
          mainType: data.ItemInformation.Item.MainType,
          stockItem: data.ItemInformation.Item.StockItem,
        })
          .then((response) => {
            // Add source to response data and clean item IDs
            const eventData = {
              ...response.data,
              items: response.data.items?.map((item) => ({
                ...item,
                id: item.id?.replace(/^none_/, '') || item.id, // Remove 'none_' prefix
                source: 'Configurator',
              })),
            };

            const event = new CustomEvent(CustomEvents.ADD_TO_CART, {
              detail: eventData,
            });
            window.dispatchEvent(event);

            const updateCartEvent = new CustomEvent(CustomEvents.UPDATE_SHOP_CART, {});
            window.dispatchEvent(updateCartEvent);

            emit('closed');
            //Close configurator at the moment
            //only available for new configurator
            //close.close();
          })
          .catch((error) => {
            showErrorSnackbar(error);
          });
      })
      .catch((error) => {
        showErrorSnackbar(error);
      });
  };
  // Find similar products
  window.onWebConfiguratorClosedOkToWebform = function (data) {
    let codes = data.CurrentState.CurrentCoding;
    let codeStrings = '';
    let codeNames = '';
    let c = 0;
    for (c = 0; c < codes.length; c++) {
      if (c != codes.length - 1) {
        codeStrings += codes[c].ContentString + ',';
        codeNames += codes[c].CodeTypeName + ',';
      } else {
        codeStrings += codes[c].ContentString;
        codeNames += codes[c].CodeTypeName;
      }
    }
    let codeQueryString = '?codeNames=' + codeNames + '&codes=' + codeStrings;
    let articleID = '';
    if (!data.ExtendedInformation.ItemInformation.Item.IsOrmigNumber) {
      articleID = data.ExtendedInformation.ItemInformation.Item.ItemId;
    }
    if (articleID == '' || articleID == 'undefined') {
      articleID = codeStrings;
    }
    const event = new CustomEvent(CustomEvents.ADD_TO_LOCAL_STORAGE, {
      detail: {
        id: articleID,
        detailText: data.CurrentState.CurrentDescriptionLong,
      },
    });
    window.dispatchEvent(event);
    // add delay to prevent NS_BINDING_ABORTED in firefox
    window.setTimeout(() => {
      window.location.href = localConfig.orderFormUrl + codeQueryString + '&article=' + articleID;
    }, delay.value);
  };
  // Initialize configurator
  window.WikaProdConf.initProduktConfigurator(
    {
      containerselector: '.Configurator__app',
      baseUrl: localConfig.baseUrl,
      optionGraphicSettings: {
        enabled: true,
        publicShareBaseUrl: localConfig.publicShareBaseUrl,
      },
      webservice_url: localConfig.webservice_url,
      language: localConfig.language,
      loginname: localConfig.loginname,
      websessionid: localConfig.websessionid,
      plannumber: localConfig.plannumber,
      maxRangeQuantity: localConfig.maxRangeQuantity,

      showtypecode: localConfig.showtypecode,
      showtexts: localConfig.showtexts,
      showadminwidget: localConfig.showadminwidget,

      visualmode: localConfig.visualmode,
      showIbDLink: localConfig.showIbDLink,
      disableExtBtn1: !window.shopAvailable || window.instance === 'shop',
      allowincomplete: localConfig.allowincomplete,
      hastagnumbers: localConfig.hastagnumbers,
      allowcodechange: localConfig.allowcodechange,
      allowgroupmode: localConfig.allowgroupmode,
      modeloritemid: localConfig.modeloritemid,
      modelcodeobj: localConfig.modelcodeobj,
      button_definitions: [
        {
          text: 'resume',
          className: 'primary',
          disabledWhileIncomplete: true,
          onClick: function (app) {
            const res = app.getResults();
            res
              .then(function (data) {
                window.onWebConfiguratorClosedOk(data);
              })
              .fail((error, data) => console.error(error, data));
          },
        },
      ],
    },
    true,
  );
};

const getParameter = (param) => {
  const params = window.location.search.substr(1).split('&');

  for (const element of params) {
    const p = element.split('=');

    if (p[0] == param) {
      return decodeURIComponent(p[1]);
    }
  }
  return false;
};

//Old Configurator
const handleInitWithWikaProdConf = () => {
  if (window.WikaProdConf) {
    init(reactiveConfig.value);
  } else {
    const timeoutForWikaProdConf = setTimeout(() => {
      handleInitWithWikaProdConf();
      clearTimeout(timeoutForWikaProdConf);
    }, 500);
  }
};

//New Configurator with new JS Libary
const handleInitWithWikaProdConfNew = () => {
  if (window.Configurator) {
    // Check if a code/configuratin is provided by url
    let initByCode = false;
    let codeString = getParameter('codestring');
    if (codeString) {
      initByCode = true;
      let codeType = getParameter('codetype');
      reactiveConfig.value.modelcodeobj = [
        {
          TypeObject: {
            CodeType: codeType || '',
            CodeString: codeString,
          },
        },
      ];
    }
    // Check if multiple modelcodes were provided - if not initialize directly
    if (reactiveConfig.value.model_variants.length == 1 || initByCode) {
      reactiveConfig.value.Id = initByCode ? '' : reactiveConfig.value.model_variants[0].model;
    }
    initNew(reactiveConfig.value);
  } else {
    const timeoutForWikaProdConf = setTimeout(() => {
      handleInitWithWikaProdConfNew();
      clearTimeout(timeoutForWikaProdConf);
    }, 500);
  }
};

onMounted(() => {
  // Check if a code/configuratin is provided by url
  let initByCode = false;
  let codeString = getParameter('codestring');
  if (codeString) {
    initByCode = true;
    let codeType = getParameter('codetype');
    reactiveConfig.value.modelcodeobj = [
      {
        TypeObject: {
          CodeType: codeType || '',
          CodeString: codeString,
        },
      },
    ];
  }

  // Check if multiple modelcodes were provided - if not initialize directly
  if (reactiveConfig.value.modelcodes.length == 1 || initByCode) {
    reactiveConfig.value.modeloritemid = initByCode ? '' : reactiveConfig.value.modelcodes[0];
    if (!reactiveConfig.value.newConfigurator) {
      handleInitWithWikaProdConf();
    }
  }

  if (reactiveConfig.value.newConfigurator) {
    const scripts = ['/shared/configurator/configurator.js'];
    scripts.forEach((script) => {
      let tag = document.head.querySelector(`[src="${script}"`);
      if (!tag) {
        tag = document.createElement('script');
        tag.setAttribute('defer', 'defer');
        tag.setAttribute('src', script);
        tag.setAttribute('type', 'text/javascript');
        document.head.appendChild(tag);
      }
    });

    window.addEventListener('buttonClickStartChat', function (event) {
      if (event.detail !== undefined) {
        if (window?.Microsoft?.Omnichannel?.LiveChatWidget?.SDK?.startChat) {
          setTimeout(() => {
            window.Microsoft.Omnichannel.LiveChatWidget.SDK.startChat();
            wtTrack({ cat: 'LIVECHATBUTTONCLICK' });
            event.detail.close();
            return false;
          }, 100);
        }
      }
    });

    let tabParam = getParameter('tab');
    if (tabParam && tabParam.includes('configurator')) {
      handleInitWithWikaProdConfNew();
    }

    window.addEventListener('openItemIdConfigurator', (event) => {
      if (event.detail.source == 'btnItemIdConfigurator') {
        reactiveConfig.value.model_variants = event.detail.modelcodes;
        initNew(reactiveConfig.value);
      }
    });
  }
});
function showErrorSnackbar(error) {
  const serverMsg = error?.error || error?.message || 'Error adding item to the basket.';
  const serverCode = error?.data?.code || error?.data?.title || '';
  const serverTransIdCode = error?.data?.translationIdCode;
  const serverTransIdMsg = error?.data?.translationIdMessage;

  const msg = `${translations.value[serverTransIdMsg] || serverMsg}\n\n`;

  if (serverCode) {
    errorMessage.value = translations.value[serverTransIdCode] || serverCode + ' - ' + msg;
  } else {
    errorMessage.value +=
      translations.value['addItem.genericError'] ||
      'Technical error – the item could not be added. Please try again later.' +
        ' <br><br> Technical information: ' +
        '<br><span style="margin-top: 12px;padding: 10px 12px;background: rgba(0, 0, 0, 0.05);' +
        'border-left: 3px solid #b71c1c;font-family: Courier New, monospace;font-size: 0.85em;white-space: pre-wrap;color: #4a2b2b;">' +
        '<span style="font-weight: bold;color: #b71c1c;">General error: </span>' +
        msg +
        '</span>';
  }
  addItemFailed.value = true;
  if (error?.data) {
    emit('error', error?.data);
  }
  console.error('Configurator API Error: ' + error);
  if (error?.data && error?.data?.ItemInformation) {
    console.error('Error with ItemData: ' + JSON.stringify(error?.data?.ItemInformation, null, 2));
  }
  if (reactiveConfig.value.newConfigurator) {
    window.Configurator.$loading().close();
  }
}
</script>

<style module lang="scss">
@forward './Configurator.scss';
</style>
